import {
  hasFieldValue,
  shouldRevealQuestionnaireErrors,
  useQuestionnaireFormik,
} from '../../../../../common/hooks';
import { onboardingQuestionnaireActions } from '../../../../../../../store';
import { useOnboardingQuestionnaireLoading } from '../../../../hooks';
import { INSURANCE_TYPE_TAB_IDS, INSURANCE_TYPE_TITLES } from '../constants';
import { INSURANCE_FORM_SCHEMA } from '../validationSchema';
import { hasInsuranceNameAndMemberId, isExtractionInProgress } from './utils';
import useInsuranceCardDocumentUpload from './useInsuranceCardDocumentUpload';
import { useInsuranceCardExtractionEntry } from './useInsuranceExtractionSelectors';
import useInsuranceDateBounds from './useInsuranceDateBounds';
import useInsuranceInitialValues from './useInsuranceInitialValues';
import useMarkInsuranceExtractionReviewed from './useMarkInsuranceExtractionReviewed';
import usePolicyholderAddressSync from './usePolicyholderAddressSync';
import useSaveInsuranceOnBlur from './useSaveInsuranceOnBlur';
import useUpsertInsuranceLoading from './useUpsertInsuranceLoading';

import type { InsuranceType } from '../../../../../../../utils/types';
import type {
  InsuranceCardFieldsSectionProps,
  InsuranceInformationFieldsProps,
  PolicyholderInformationFieldsProps,
} from '../components/types';

export default function useInsuranceFormState(insuranceType: InsuranceType) {
  const tabId = INSURANCE_TYPE_TAB_IDS[insuranceType];
  const title = INSURANCE_TYPE_TITLES[insuranceType];
  const initialValues = useInsuranceInitialValues(insuranceType);
  const isLoading = useOnboardingQuestionnaireLoading();
  const isUpdating = useUpsertInsuranceLoading(insuranceType);
  const extractionEntry = useInsuranceCardExtractionEntry(insuranceType);
  const isExtracting = extractionEntry != null && isExtractionInProgress(extractionEntry.status);
  const areFieldsDisabled = isLoading || isExtracting;
  useMarkInsuranceExtractionReviewed(insuranceType);
  const {
    backFileName,
    backFileType,
    backPreviewLoading,
    backPreviewUrl,
    backUploadButtonLabel,
    deleteDocumentLoading,
    frontFileName,
    frontFileType,
    frontPreviewLoading,
    frontPreviewUrl,
    frontUploadButtonLabel,
    onUploadBack,
    onUploadFront,
    uploadDocumentLoading,
  } = useInsuranceCardDocumentUpload(insuranceType);
  const hasCardUploads = Boolean(frontFileName || backFileName);
  const hasBothCardSides = Boolean(frontFileName && backFileName);
  const formik = useQuestionnaireFormik({
    tabId,
    initialValues,
    validationSchema: INSURANCE_FORM_SCHEMA,
    extraRequirementMet: hasInsuranceNameAndMemberId(initialValues) || hasBothCardSides,
    hasNonFormikSavedValues: hasCardUploads,
    updateTabStatus: onboardingQuestionnaireActions.updateOnboardingTabStatus,
  });
  const isCardRequirementUnmet = !hasInsuranceNameAndMemberId(formik.values) && !hasBothCardSides;
  const { saveFieldOnBlur, saveInsuranceFields } = useSaveInsuranceOnBlur(
    formik,
    initialValues,
    insuranceType,
  );
  const { handleSameAsMemberChange, handleAddressFieldChange, saveAddressFieldOnChange } =
    usePolicyholderAddressSync({ formik, saveFieldOnBlur, saveInsuranceFields });
  const isDocumentActionLoading = uploadDocumentLoading || deleteDocumentLoading;
  const { insuranceStartDisabledDate, insuranceEndDisabledDate } = useInsuranceDateBounds(formik);
  const cardFields: InsuranceCardFieldsSectionProps = {
    backFileName,
    backFileType,
    backPreviewLoading,
    backPreviewUrl,
    backUploadButtonLabel,
    disabled: isLoading || isDocumentActionLoading || isExtracting,
    frontFileName,
    frontFileType,
    frontPreviewLoading,
    frontPreviewUrl,
    frontUploadButtonLabel,
    insuranceType,
    isExtracting,
    onUploadBack,
    onUploadFront,
    showHelperText: isCardRequirementUnmet,
    showRequiredError:
      isCardRequirementUnmet &&
      shouldRevealQuestionnaireErrors({
        submitCount: formik.submitCount,
        touched: formik.touched,
        hasSavedValues: hasFieldValue(initialValues) || hasCardUploads,
      }),
  };
  const informationFields: InsuranceInformationFieldsProps = {
    disabled: areFieldsDisabled,
    formik,
    handleAddressFieldChange,
    insuranceEndDisabledDate,
    insuranceStartDisabledDate,
    isExtracting,
    saveAddressFieldOnChange,
    saveFieldOnBlur,
  };
  const policyholderFields: PolicyholderInformationFieldsProps = {
    disabled: areFieldsDisabled,
    formik,
    handleAddressFieldChange,
    handleSameAsMemberChange,
    saveAddressFieldOnChange,
    saveFieldOnBlur,
  };

  return {
    title,
    formik,
    isLoading,
    isUpdating,
    isDocumentActionLoading,
    cardFields,
    informationFields,
    policyholderFields,
  };
}
